import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, ExternalLink, Image as ImageIcon, LogOut, Save, Upload } from "lucide-react";
import { isImageField, normalizeCmsContent, shouldEditField } from "@/cms/cmsModel";
import { useCmsDocument } from "@/cms/CmsContentProvider";
import { LanguageToggle } from "@/components/LanguageToggle";

const TAB_LABELS = {
  de: { global: "Allgemein", social: "Social-Media-Links", home: "Startseite", pages: "Weitere Seiten", servicePages: "Leistungsseiten", common: "Gemeinsame Texte" },
  ar: { global: "عام", social: "روابط السوشيال ميديا", home: "الصفحة الرئيسية", pages: "الصفحات الأخرى", servicePages: "صفحات الخدمات", common: "النصوص المشتركة" },
};

const HUMAN_LABELS = {
  site: "Firmendaten", hotlines: "Hotlines", openingHours: "Öffnungszeiten",
  navLinks: "Navigation", categories: "Formularbereiche", services: "Leistungen",
  benefits: "Vorteile", reviews: "Bewertungen", socialLinks: "Soziale Medien",
  homeStatement: "Statement", homeShowcase: "Startseiten-Bereiche", homeVideo: "Video-Bereich",
  homeStats: "Kennzahlen", page: "Seitentexte", about: "Über uns", partner: "Partnernetzwerk",
  contact: "Kontakt", servicePages: "Leistungsseiten", common: "Gemeinsame Texte",
  title: "Titel", subtitle: "Untertitel", text: "Text", heading: "Überschrift",
  eyebrow: "Kleine Überschrift", image: "Bild", src: "Bild", poster: "Vorschaubild",
  background: "Hintergrundbild", alt: "Bildbeschreibung", label: "Bezeichnung",
  href: "Link zur Seite",
  name: "Name", city: "Stadt", short: "Kurztext", description: "Beschreibung",
  intro: "Einleitung", paragraphs: "Absätze", bullets: "Listenpunkte", gallery: "Galerie",
  cta: "Kontaktbereich", detail: "Detailbereich", media: "Medien", form: "Formular",
  scope: "Leistungsumfang", steps: "Ablauf", items: "Einträge", hero: "Kopfbereich",
};

const HUMAN_LABELS_AR = {
  site: "بيانات الشركة", hotlines: "أرقام الهاتف", openingHours: "ساعات العمل",
  navLinks: "التنقل", categories: "مجالات النموذج", services: "الخدمات",
  benefits: "المزايا", reviews: "التقييمات", socialLinks: "وسائل التواصل",
  homeStatement: "البيان", homeShowcase: "أقسام الرئيسية", homeVideo: "قسم الفيديو",
  homeStats: "الإحصاءات", page: "نصوص الصفحة", about: "من نحن", partner: "شبكة الشركاء",
  contact: "التواصل", servicePages: "صفحات الخدمات", common: "النصوص المشتركة",
  title: "العنوان", subtitle: "العنوان الفرعي", text: "النص", heading: "عنوان القسم",
  eyebrow: "العنوان الصغير", image: "الصورة", src: "الصورة", poster: "صورة المعاينة",
  background: "صورة الخلفية", alt: "وصف الصورة", label: "التسمية",
  href: "رابط الصفحة",
  name: "الاسم", city: "المدينة", short: "النص المختصر", description: "الوصف",
  intro: "المقدمة", paragraphs: "الفقرات", bullets: "النقاط", gallery: "معرض الصور",
  cta: "قسم التواصل", detail: "التفاصيل", media: "الوسائط", form: "النموذج",
  scope: "نطاق الخدمة", steps: "الخطوات", items: "العناصر", hero: "رأس الصفحة",
};

const ADMIN_COPY = {
  de: {
    loading: "Administration wird geladen …", title: "Administration", subtitle: "Texte und Bilder der Website bearbeiten",
    notConfigured: "Die Zugangsdaten müssen zuerst in Netlify eingerichtet werden.", username: "Benutzername", password: "Passwort",
    showPassword: "Passwort anzeigen", hidePassword: "Passwort ausblenden", login: "Anmelden", loginFailed: "Anmeldung fehlgeschlagen.",
    forgotPassword: "Passwort vergessen?", forgotTitle: "Passwort zurücksetzen", forgotSubtitle: "Geben Sie die hinterlegte E-Mail-Adresse ein.",
    email: "E-Mail-Adresse", sendResetLink: "Link senden", sending: "Wird gesendet …", backToLogin: "Zurück zur Anmeldung",
    resetEmailSent: "Falls die Adresse hinterlegt ist, wurde ein Link zum Zurücksetzen gesendet. Bei Bedarf können Sie sofort einen neuen Link anfordern.",
    newPassword: "Neues Passwort", confirmPassword: "Passwort wiederholen", savePassword: "Passwort speichern",
    passwordHint: "Mindestens 8 Zeichen", passwordMismatch: "Die Passwörter stimmen nicht überein.",
    resetSuccess: "Das Passwort wurde geändert. Sie können sich jetzt anmelden.", resetFailed: "Das Passwort konnte nicht geändert werden.",
    dashboard: "Website-Verwaltung", signedIn: "Angemeldet als", website: "Website", logout: "Abmelden",
    editorHint: "Vorhandene Texte, Bilder und Social-Media-Links können bearbeitet werden.", save: "Speichern & veröffentlichen", saving: "Speichert …",
    saved: "Änderungen wurden veröffentlicht.", saveFailed: "Speichern fehlgeschlagen.", clear: "Text leeren",
    noImage: "Kein Bild", replaceImage: "Bild ersetzen", uploading: "Wird hochgeladen …",
    imageHelp: "JPG, PNG, WebP, AVIF oder GIF · maximal 5 MB", uploadFailed: "Bild konnte nicht hochgeladen werden.",
    uploadDone: "Bild wurde ersetzt. Bitte anschließend speichern.", languageTitle: "Deutsche Inhalte", tooMany: "Zu viele Versuche. Bitte 15 Minuten warten.",
  },
  ar: {
    loading: "جارٍ تحميل لوحة التحكم…", title: "لوحة التحكم", subtitle: "تعديل نصوص وصور الموقع",
    notConfigured: "يجب أولاً إعداد بيانات الدخول في Netlify.", username: "اسم المستخدم", password: "كلمة المرور",
    showPassword: "إظهار كلمة المرور", hidePassword: "إخفاء كلمة المرور", login: "تسجيل الدخول", loginFailed: "فشل تسجيل الدخول.",
    forgotPassword: "نسيت كلمة المرور؟", forgotTitle: "إعادة تعيين كلمة المرور", forgotSubtitle: "أدخل البريد الإلكتروني المسجل للإدارة.",
    email: "البريد الإلكتروني", sendResetLink: "إرسال الرابط", sending: "جارٍ الإرسال…", backToLogin: "العودة إلى تسجيل الدخول",
    resetEmailSent: "إذا كان البريد مسجلاً، فقد تم إرسال رابط إعادة تعيين كلمة المرور. يمكنك طلب رابط جديد فورًا عند الحاجة.",
    newPassword: "كلمة المرور الجديدة", confirmPassword: "تأكيد كلمة المرور", savePassword: "حفظ كلمة المرور",
    passwordHint: "8 أحرف على الأقل", passwordMismatch: "كلمتا المرور غير متطابقتين.",
    resetSuccess: "تم تغيير كلمة المرور. يمكنك تسجيل الدخول الآن.", resetFailed: "تعذر تغيير كلمة المرور.",
    dashboard: "إدارة الموقع", signedIn: "تم تسجيل الدخول باسم", website: "فتح الموقع", logout: "تسجيل الخروج",
    editorHint: "يمكن تعديل النصوص والصور وروابط وسائل التواصل الموجودة.", save: "حفظ ونشر", saving: "جارٍ الحفظ…",
    saved: "تم نشر التغييرات.", saveFailed: "تعذر الحفظ.", clear: "مسح النص",
    noImage: "لا توجد صورة", replaceImage: "استبدال الصورة", uploading: "جارٍ رفع الصورة…",
    imageHelp: "JPG أو PNG أو WebP أو AVIF أو GIF · بحد أقصى 5 MB", uploadFailed: "تعذر رفع الصورة.",
    uploadDone: "تم استبدال الصورة. يرجى الحفظ بعد ذلك.", languageTitle: "المحتوى العربي", tooMany: "محاولات كثيرة. يرجى الانتظار 15 دقيقة.",
  },
};

const labelFor = (key, locale) => {
  if (/^\d+$/.test(String(key))) return locale === "ar" ? `العنصر ${Number(key) + 1}` : `Eintrag ${Number(key) + 1}`;
  const labels = locale === "ar" ? HUMAN_LABELS_AR : HUMAN_LABELS;
  return labels[key] || String(key).replace(/([a-z])([A-Z])/g, "$1 $2");
};

const clone = (value) => JSON.parse(JSON.stringify(value));
const HIDDEN_ADMIN_FIELDS = new Set(["common.footer.developer"]);

const setAtPath = (source, path, value) => {
  const next = clone(source);
  const parts = path.split(".");
  let cursor = next;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) cursor[part] = value;
    else cursor = cursor[part];
  });
  return next;
};

const EditableField = ({ path, value, onChange, onUpload, uploading, locale, copy }) => {
  const key = path.split(".").pop();
  const image = isImageField(path, value);
  const socialLink = /^global\.socialLinks\.(0|1|2)\.href$/i.test(path);
  if (HIDDEN_ADMIN_FIELDS.has(path)) return null;
  if (!shouldEditField(path, value)) return null;

  if (image) {
    return (
      <div className="rounded-2xl border border-[#d5dee8] bg-white p-4">
        <label className="mb-3 block text-sm font-semibold text-[#193d6d]">{labelFor(key, locale)}</label>
        <div className="grid gap-4 sm:grid-cols-[160px_1fr] sm:items-center">
          <div className="aspect-[4/3] overflow-hidden rounded-xl bg-[#e7edf5]">
            {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="m-auto mt-12 text-[#8290a0]" />}
          </div>
          <div>
            <p className="mb-3 break-all text-xs text-[#6e7683]">{value || copy.noImage}</p>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-[#193d6d] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#244f85]">
              <Upload size={16} /> {uploading ? copy.uploading : copy.replaceImage}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
                className="hidden"
                disabled={uploading}
                onChange={(event) => event.target.files?.[0] && onUpload(path, event.target.files[0])}
              />
            </label>
            <p className="mt-2 text-xs text-[#8290a0]">{copy.imageHelp}</p>
          </div>
        </div>
      </div>
    );
  }

  const multiline = value.length > 70 || /(text|subtitle|description|intro|paragraph)/i.test(key);
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label className="text-sm font-semibold text-[#193d6d]">{labelFor(key, locale)}</label>
        {value && <button type="button" onClick={() => onChange(path, "")} className="text-xs text-[#7a1516] hover:underline">{copy.clear}</button>}
      </div>
      {multiline ? (
        <textarea value={value} onChange={(event) => onChange(path, event.target.value)} rows={4} className="w-full rounded-xl border border-[#cbd6e2] bg-white px-3 py-2.5 text-[#26303c] outline-none focus:border-[#193d6d] focus:ring-2 focus:ring-[#193d6d]/15" />
      ) : (
        <input type={socialLink ? "url" : "text"} dir={socialLink ? "ltr" : undefined} value={value} onChange={(event) => onChange(path, event.target.value)} className="w-full rounded-xl border border-[#cbd6e2] bg-white px-3 py-2.5 text-[#26303c] outline-none focus:border-[#193d6d] focus:ring-2 focus:ring-[#193d6d]/15" />
      )}
    </div>
  );
};

const EditorTree = ({ value, path, onChange, onUpload, uploadingPath, locale, copy, depth = 0 }) => {
  if (typeof value === "string") {
    return <EditableField path={path} value={value} onChange={onChange} onUpload={onUpload} uploading={uploadingPath === path} locale={locale} copy={copy} />;
  }
  if (!value || typeof value !== "object") return null;

  return (
    <div className={depth ? "space-y-4 rounded-2xl border border-[#d5dee8] bg-[#f8fafc] p-4" : "space-y-5"}>
      {Object.entries(value).map(([key, child]) => {
        const childPath = path ? `${path}.${key}` : key;
        if (typeof child === "string") {
          return <EditableField key={childPath} path={childPath} value={child} onChange={onChange} onUpload={onUpload} uploading={uploadingPath === childPath} locale={locale} copy={copy} />;
        }
        if (!child || typeof child !== "object") return null;
        return (
          <section key={childPath}>
            <h3 className={`${depth < 1 ? "text-xl" : "text-base"} mb-3 font-heading font-bold text-[#193d6d]`}>{/^\d+$/.test(key) && (child.label || child.title) ? (child.label || child.title) : labelFor(key, locale)}</h3>
            <EditorTree value={child} path={childPath} onChange={onChange} onUpload={onUpload} uploadingPath={uploadingPath} locale={locale} copy={copy} depth={depth + 1} />
          </section>
        );
      })}
    </div>
  );
};

export default function Admin() {
  const { document, setDocument, locale } = useCmsDocument();
  const copy = ADMIN_COPY[locale];
  const [session, setSession] = useState({ loading: true, authenticated: false, configured: true });
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [authView, setAuthView] = useState(() => new URLSearchParams(window.location.search).get("reset") ? "reset" : "login");
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState({ password: "", confirm: "" });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [contentByLocale, setContentByLocale] = useState(() => clone(normalizeCmsContent(document.data)));
  const content = contentByLocale[locale];
  const [activeTab, setActiveTab] = useState("global");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingPath, setUploadingPath] = useState("");

  useEffect(() => { setContentByLocale(clone(normalizeCmsContent(document.data))); }, [document]);
  useEffect(() => {
    fetch("/api/cms/auth/session", { credentials: "include" })
      .then((response) => response.json())
      .then((data) => setSession({ loading: false, ...data }))
      .catch(() => setSession({ loading: false, authenticated: false, configured: false }));
  }, []);

  const tabs = useMemo(() => {
    const contentTabs = Object.keys(content).filter((key) => TAB_LABELS[locale][key] && key !== "global");
    return ["global", ...contentTabs, "social"];
  }, [content, locale]);

  const editorValue = useMemo(() => {
    if (activeTab === "social") return { socialLinks: content.global.socialLinks.slice(0, 3) };
    if (activeTab === "global") {
      const { socialLinks: _socialLinks, ...generalContent } = content.global;
      return generalContent;
    }
    return content[activeTab];
  }, [activeTab, content]);

  const editorPath = activeTab === "social" ? "global" : activeTab;

  const login = async (event) => {
    event.preventDefault();
    setMessage("");
    setAuthBusy(true);
    try {
      const response = await fetch("/api/cms/auth/login", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });
      const data = await response.json();
      if (!response.ok) return setMessage(response.status === 429 ? copy.tooMany : copy.loginFailed);
      setCredentials({ username: "", password: "" });
      setSession({ loading: false, authenticated: true, configured: true, username: data.username });
    } catch {
      setMessage(copy.loginFailed);
    } finally {
      setAuthBusy(false);
    }
  };

  const requestPasswordReset = async (event) => {
    event.preventDefault();
    setMessage("");
    setAuthBusy(true);
    try {
      const response = await fetch("/api/cms/auth/request-reset", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || copy.resetFailed);
      setResetEmail("");
      setMessage(copy.resetEmailSent);
    } catch (error) {
      setMessage(error.message || copy.resetFailed);
    } finally {
      setAuthBusy(false);
    }
  };

  const submitNewPassword = async (event) => {
    event.preventDefault();
    setMessage("");
    if (resetPassword.password !== resetPassword.confirm) return setMessage(copy.passwordMismatch);
    setAuthBusy(true);
    try {
      const token = new URLSearchParams(window.location.search).get("reset") || "";
      const response = await fetch("/api/cms/auth/reset-password", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: resetPassword.password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || copy.resetFailed);
      window.history.replaceState({}, "", "/admin");
      setResetPassword({ password: "", confirm: "" });
      setShowResetPassword(false);
      setShowResetConfirm(false);
      setAuthView("login");
      setMessage(copy.resetSuccess);
    } catch (error) {
      setMessage(error.message || copy.resetFailed);
    } finally {
      setAuthBusy(false);
    }
  };

  const logout = async () => {
    await fetch("/api/cms/auth/logout", { method: "POST", credentials: "include" });
    setSession({ loading: false, authenticated: false, configured: true });
  };

  const change = (path, value) => setContentByLocale((current) => {
    if (/^global\.socialLinks\.(0|1|2)\.href$/i.test(path)) {
      return {
        de: setAtPath(current.de, path, value),
        ar: setAtPath(current.ar, path, value),
      };
    }
    return { ...current, [locale]: setAtPath(current[locale], path, value) };
  });

  const upload = async (path, file) => {
    setMessage("");
    setUploadingPath(path);
    const slot = `${locale}-${path}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const key = `${slot}-${Date.now()}`;
    try {
      const response = await fetch(`/api/cms/media?key=${encodeURIComponent(key)}`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": file.type }, body: file,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || copy.uploadFailed);
      change(path, data.url);
      setMessage(copy.uploadDone);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setUploadingPath("");
    }
  };

  const save = async () => {
    setSaving(true); setMessage("");
    try {
      const response = await fetch("/api/cms/content", {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          revision: document.revision || 0,
          data: contentByLocale,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || copy.saveFailed);
      setDocument({ ...data, data: normalizeCmsContent(data.data) });
      setMessage(copy.saved);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (session.loading) return <div className="flex min-h-screen items-center justify-center bg-[#e7edf5] text-[#193d6d]">{copy.loading}</div>;

  if (!session.authenticated) {
    return (
      <main className="relative flex min-h-screen items-center justify-center bg-[#e7edf5] px-4">
        <LanguageToggle className="absolute top-4 end-4" />
        <div className="w-full max-w-md rounded-3xl border border-[#d5dee8] bg-white p-8 shadow-xl">
          <img src="/az-logo.png" alt="AZ Gruppe" className="mx-auto h-16 w-auto" />
          <h1 className="mt-6 text-center font-heading text-3xl font-bold text-[#193d6d]">{authView === "login" ? copy.title : copy.forgotTitle}</h1>
          <p className="mt-2 text-center text-sm text-[#6e7683]">{authView === "login" ? copy.subtitle : copy.forgotSubtitle}</p>
          {!session.configured && <p className="mt-5 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">{copy.notConfigured}</p>}
          {message && <p className="mt-4 text-sm text-[#7a1516]">{message}</p>}

          {authView === "login" && (
            <form onSubmit={login}>
              <label className="mt-6 block text-sm font-semibold text-[#26303c]">{copy.username}</label>
              <input autoComplete="username" required value={credentials.username} onChange={(e) => setCredentials((v) => ({ ...v, username: e.target.value }))} className="mt-1.5 w-full rounded-xl border border-[#cbd6e2] px-3 py-3" />
              <label className="mt-4 block text-sm font-semibold text-[#26303c]">{copy.password}</label>
              <div className="relative mt-1.5">
                <input type={showPassword ? "text" : "password"} autoComplete="current-password" required value={credentials.password} onChange={(e) => setCredentials((v) => ({ ...v, password: e.target.value }))} className="w-full rounded-xl border border-[#cbd6e2] py-3 pl-3 pr-12" />
                <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? copy.hidePassword : copy.showPassword} aria-pressed={showPassword} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-[#526172] hover:text-[#193d6d]">
                  {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                </button>
              </div>
              <button type="button" onClick={() => { setMessage(""); setAuthView("forgot"); }} className="mt-3 text-sm font-semibold text-[#193d6d] hover:underline">{copy.forgotPassword}</button>
              <button disabled={!session.configured || authBusy} className="mt-6 w-full rounded-xl bg-[#7a1516] px-4 py-3 font-semibold text-white disabled:opacity-50">{authBusy ? copy.loading : copy.login}</button>
            </form>
          )}

          {authView === "forgot" && (
            <form onSubmit={requestPasswordReset}>
              <label className="mt-6 block text-sm font-semibold text-[#26303c]">{copy.email}</label>
              <input type="email" autoComplete="email" required value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} className="mt-1.5 w-full rounded-xl border border-[#cbd6e2] px-3 py-3" />
              <button disabled={!session.configured || authBusy} className="mt-6 w-full rounded-xl bg-[#7a1516] px-4 py-3 font-semibold text-white disabled:opacity-50">{authBusy ? copy.sending : copy.sendResetLink}</button>
              <button type="button" onClick={() => { setMessage(""); setAuthView("login"); }} className="mt-4 w-full text-sm font-semibold text-[#193d6d] hover:underline">{copy.backToLogin}</button>
            </form>
          )}

          {authView === "reset" && (
            <form onSubmit={submitNewPassword}>
              <label className="mt-6 block text-sm font-semibold text-[#26303c]">{copy.newPassword}</label>
              <div className="relative mt-1.5">
                <input type={showResetPassword ? "text" : "password"} autoComplete="new-password" minLength={8} required value={resetPassword.password} onChange={(event) => setResetPassword((current) => ({ ...current, password: event.target.value }))} className="w-full rounded-xl border border-[#cbd6e2] py-3 pe-12 ps-3" />
                <button type="button" onClick={() => setShowResetPassword((visible) => !visible)} aria-label={showResetPassword ? copy.hidePassword : copy.showPassword} aria-pressed={showResetPassword} className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-[#526172] hover:text-[#193d6d]">
                  {showResetPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-[#6e7683]">{copy.passwordHint}</p>
              <label className="mt-4 block text-sm font-semibold text-[#26303c]">{copy.confirmPassword}</label>
              <div className="relative mt-1.5">
                <input type={showResetConfirm ? "text" : "password"} autoComplete="new-password" minLength={8} required value={resetPassword.confirm} onChange={(event) => setResetPassword((current) => ({ ...current, confirm: event.target.value }))} className="w-full rounded-xl border border-[#cbd6e2] py-3 pe-12 ps-3" />
                <button type="button" onClick={() => setShowResetConfirm((visible) => !visible)} aria-label={showResetConfirm ? copy.hidePassword : copy.showPassword} aria-pressed={showResetConfirm} className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-[#526172] hover:text-[#193d6d]">
                  {showResetConfirm ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                </button>
              </div>
              <button disabled={!session.configured || authBusy} className="mt-6 w-full rounded-xl bg-[#7a1516] px-4 py-3 font-semibold text-white disabled:opacity-50">{authBusy ? copy.saving : copy.savePassword}</button>
              <button type="button" onClick={() => { window.history.replaceState({}, "", "/admin"); setMessage(""); setAuthView("login"); }} className="mt-4 w-full text-sm font-semibold text-[#193d6d] hover:underline">{copy.backToLogin}</button>
            </form>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#e7edf5]">
      <header className="sticky top-0 z-30 border-b border-[#d5dee8] bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3"><img src="/az-logo.png" alt="" className="h-10 w-auto" /><div><h1 className="font-heading text-lg font-bold text-[#193d6d]">{copy.dashboard}</h1><p className="text-xs text-[#6e7683]">{copy.signedIn} {session.username}</p></div></div>
          <div className="flex gap-2">
            <LanguageToggle />
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-[#cbd6e2] px-3 py-2 text-sm font-semibold text-[#193d6d]"><ExternalLink size={15} /> {copy.website}</a>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-xl border border-[#cbd6e2] px-3 py-2 text-sm font-semibold text-[#7a1516]"><LogOut size={15} /> {copy.logout}</button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[240px_1fr]">
        <aside className="h-fit rounded-2xl border border-[#d5dee8] bg-white p-3 lg:sticky lg:top-24">
          <p className="mb-3 px-3 text-xs font-bold uppercase tracking-wide text-[#7a1516]">{copy.languageTitle}</p>
          {tabs.map((tab) => <button key={tab} onClick={() => setActiveTab(tab)} className={`mb-1 w-full rounded-xl px-4 py-3 text-start text-sm font-semibold ${activeTab === tab ? "bg-[#193d6d] text-white" : "text-[#26303c] hover:bg-[#e7edf5]"}`}>{TAB_LABELS[locale][tab]}</button>)}
        </aside>
        <section className="min-w-0 rounded-3xl border border-[#d5dee8] bg-white p-5 shadow-sm sm:p-7">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-[#e7edf5] pb-5">
            <div><h2 className="font-heading text-2xl font-bold text-[#193d6d]">{TAB_LABELS[locale][activeTab]}</h2><p className="mt-1 text-sm text-[#6e7683]">{copy.editorHint}</p></div>
            <button onClick={save} disabled={saving || Boolean(uploadingPath)} className="inline-flex items-center gap-2 rounded-xl bg-[#7a1516] px-5 py-3 font-semibold text-white disabled:opacity-50"><Save size={17} /> {saving ? copy.saving : copy.save}</button>
          </div>
          {message && <p className="mb-5 rounded-xl bg-[#e7edf5] px-4 py-3 text-sm text-[#193d6d]">{message}</p>}
          <EditorTree value={editorValue} path={editorPath} onChange={change} onUpload={upload} uploadingPath={uploadingPath} locale={locale} copy={copy} />
        </section>
      </div>
    </main>
  );
}
